<script setup lang="ts">
import HelpModalItemsList from "@/assets/ts/compare/HelpModalItemsList";
import { useConditionStore, ConditionState } from '@/stores/conditionStore'
import { useValidationWatcher, validateEmail } from "@/composables/validationCheck";

// プロパティの定義
const props = defineProps({
  masterDataList: {
    type: Object,
    required: true,
  },
});

const conditionStore = useConditionStore();

// 各フィルターの選択値を保持するリアクティブな変数
const buildingTypeValue = ref<number>(0);
const buildingStructureValue = ref<number>(0);
const buildingAgeValue = ref<number>(0);
const prefValue = ref<number>(0);
const buildingInsAmountValue = ref<number|null>(null);
const householdInsAmountValue = ref<number|null>(null);
const hasEarthquakeInsuranceValue = ref<boolean>(false);
const seismicYearsDiscountValue = ref<number>(0);
const windHailSnowDamageValue = ref<boolean>(true);
const floodDisasterValue = ref<boolean>(false);
const theftFlyingWaterDisturbanceValue = ref<boolean>(true);
const damageContaminationValue = ref<boolean>(false);
const name1 = ref<string>('');
const name2 = ref<string>('');
const nameKana1 = ref<string>('');
const nameKana2 = ref<string>('');
const email = ref<string>('');
const nameKanjiError = ref<string>('');
const nameKanaError = ref<string>('');
const emailError = ref<string>('');
const emailTouched = ref(false); // メールアドレス欄のblur後にバリデーションチェック済みとするフラグ

onMounted(() => {
  buildingTypeValue.value = conditionStore.buildingType ?? 0;
  nextTick(() => {
    buildingStructureValue.value = conditionStore.buildingStructure ?? 0;
  });
  buildingAgeValue.value = conditionStore.buildingAge ?? 0;
  prefValue.value = conditionStore.prefId ?? 0;
  buildingInsAmountValue.value = conditionStore.buildingInsAmount ?? null;
  householdInsAmountValue.value = conditionStore.householdInsAmount ?? null;
  hasEarthquakeInsuranceValue.value = conditionStore.hasEarthquakeInsurance;
  windHailSnowDamageValue.value = conditionStore.windHailSnowDamage;
  floodDisasterValue.value = conditionStore.floodDisaster;
  theftFlyingWaterDisturbanceValue.value = conditionStore.theftFlyingWaterDisturbance;
  damageContaminationValue.value = conditionStore.damageContamination;
  seismicYearsDiscountValue.value = conditionStore.seismicYearsDiscount ?? 0;
  name1.value = conditionStore.name1 ?? '';
  name2.value = conditionStore.name2 ?? '';
  nameKana1.value = conditionStore.nameKana1 ?? '';
  nameKana2.value = conditionStore.nameKana2 ?? '';
  email.value = conditionStore.email ?? '';
  // メールアドレス欄に値が入っている場合バリデーションを実行
  if (email.value.trim() !== '') {
    emailError.value = validateEmail(email.value);
    emailTouched.value = true;
  } else {
    emailError.value = '';
    emailTouched.value = false;
  }
});

// 各フィルターのオプションリストを保持するリアクティブな変数
const buildingTypeList = computed(() => props.masterDataList.buildingTypeList);
const mansionBuildingStructureList = computed(() => props.masterDataList.mansionBuildingStructureList);
const houseBuildingStructureList = computed(() => props.masterDataList.houseBuildingStructureList);
// 初期表示は戸建て用、マンション選択時はマンション用
const buildingStructureList = computed(() => {
  if (buildingTypeValue.value === 0 || Number(buildingTypeValue.value) === 2) {
    // 初期表示または戸建て選択時
    return houseBuildingStructureList.value;
  }
  if (Number(buildingTypeValue.value) === 1) {
    // マンション選択時
    return mansionBuildingStructureList.value;
  }
  return [];
});
const buildingAgeList = computed(() => props.masterDataList.buildingAgeList);
const prefList = computed(() => props.masterDataList.prefList);
const mansionBuildingInsAmountList = computed(() => props.masterDataList.mansionBuildingInsAmountList);
const houseBuildingInsAmountList = computed(() => props.masterDataList.houseBuildingInsAmountList);
const buildingInsAmountList = computed(() => {
  // 選択中の建物種類によって参照するリストを切り替える
  if (Number(buildingTypeValue.value) === 1) return mansionBuildingInsAmountList.value;
  if (Number(buildingTypeValue.value) === 2) return houseBuildingInsAmountList.value;
  return [];
});
const mansionHouseholdInsAmountList = computed(() => props.masterDataList.mansionHouseholdInsAmountList);
const houseHouseholdInsAmountList = computed(() => props.masterDataList.houseHouseholdInsAmountList);
const householdInsAmountList = computed(() => {
  // 選択中の建物種類によって参照するリストを切り替える
  if (Number(buildingTypeValue.value) === 1) return mansionHouseholdInsAmountList.value;
  if (Number(buildingTypeValue.value) === 2) return houseHouseholdInsAmountList.value;
  return [];
});
// const seismicYearsDiscountList = computed(() => props.masterDataList.seismicYearsDiscountList); // SeismicYearsDiscountList（地震保険等級）は現在使用されていないため、コメントアウト
const helpModalItems = HelpModalItemsList;

// 建物種類の変更時に建物保険金額・家財保険金額を未選択状態にリセット
function onBuildingTypeChange() {
  buildingInsAmountValue.value = null;
  householdInsAmountValue.value = null;
}

// storeの値を更新する関数
const updateStore = <T extends keyof ConditionState>(key: T, value: any) => {
  conditionStore[key] = value as any;
};

// 各フォーム値とstoreの値を同期するためのペアを定義
const refStorePairs: [Ref<unknown>, keyof ConditionState][] = [
  [buildingTypeValue, 'buildingType'],
  [buildingStructureValue, 'buildingStructure'],
  [buildingAgeValue, 'buildingAge'],
  [prefValue, 'prefId'],
  [buildingInsAmountValue, 'buildingInsAmount'],
  [householdInsAmountValue, 'householdInsAmount'],
  [hasEarthquakeInsuranceValue, 'hasEarthquakeInsurance'],
  [seismicYearsDiscountValue, 'seismicYearsDiscount'],
  [damageContaminationValue, 'damageContamination'],
  [floodDisasterValue, 'floodDisaster'],
  [theftFlyingWaterDisturbanceValue, 'theftFlyingWaterDisturbance'],
  [windHailSnowDamageValue, 'windHailSnowDamage'],
  [name1, 'name1'],
  [name2, 'name2'],
  [nameKana1, 'nameKana1'],
  [nameKana2, 'nameKana2'],
  [email, 'email']
];

// 各フォーム値をwatchし、変更時に対応するstoreの値も更新する
refStorePairs.forEach(([refVar, storeKey]) => {
  watch(refVar, val => {
    updateStore(storeKey, val);
  });
});

// 地震保険の有無が変更されたときに、地震保険等級の値を更新
watch(hasEarthquakeInsuranceValue, (val) => {
  seismicYearsDiscountValue.value = val === true ? 1 : 0;
});

// 建物種類がマンション選択時、建物構造リストを自動でセット
watch(buildingTypeValue, (val) => {
  // 建物構造リストを取得
  const list = buildingStructureList.value;
  // マンション選択時かつ選択肢が1つだけの場合、自動でセット
  if (Number(val) === 1 && list.length === 1) {
    buildingStructureValue.value = list[0].id;
  } else {
    // それ以外は選択解除
    buildingStructureValue.value = 0;
  }
});

// 名前・カナのバリデーションチェック
useValidationWatcher([name1, name2], nameKanjiError, '標準漢字などでご入力ください');
useValidationWatcher([nameKana1, nameKana2], nameKanaError, '標準カナなどでご入力ください', { katakanaOnly: true });

// お客様の情報の入力チェック
const isNameKanjiValid = computed(() =>
  name1.value !== '' &&
  name2.value !== '' &&
  nameKanjiError.value === ''
);
const isNameKanaValid = computed(() =>
  nameKana1.value !== '' &&
  nameKana2.value !== '' &&
  nameKanaError.value === ''
);

// blur時にメールバリデーションを実行し、チェック済みフラグ（emailTouched）をtrueにする。
function validateEmailOnBlur() {
  emailTouched.value = true;
  emailError.value = validateEmail(email.value);
}

// メールアドレス欄に入力があった場合、チェック済みフラグ（emailTouched）をfalseにする。
function handleEmailInput() {
  emailTouched.value = false;
}

// blur後、メールアドレス欄の入力があって、かつエラーがない場合にOKとする
const isEmailValid = computed(() => emailTouched.value && email.value !== '' && emailError.value === '');

// 地震保険と補償内容のアコーディオン表示制御
const isEarthquakeAndCompensationOpen = ref<boolean>(false);

// アコーディオンの開閉を切り替える関数
function toggleEarthquakeAndCompensation() {
  isEarthquakeAndCompensationOpen.value = !isEarthquakeAndCompensationOpen.value;
}

// 選択されている補償内容を取得
const selectedCompensations = computed(() => {
  const items = ['火災・落雷・破裂・爆発'];
  if (windHailSnowDamageValue.value) items.push('風災・ひょう災・雪災');
  if (floodDisasterValue.value) items.push('水災');
  if (theftFlyingWaterDisturbanceValue.value) items.push('盗難・物体飛来・水ぬれ・騒じょう');
  if (damageContaminationValue.value) items.push('破損・汚損');
  return items.join('、');
});

// 所有する建物の情報ブロックの必須項目が全てOKか判定
const isInfoAllOk = computed(() =>
  buildingTypeValue.value !== 0 &&
  buildingStructureValue.value !== 0 &&
  buildingAgeValue.value !== 0 &&
  prefValue.value !== 0
);

// 保険金額・補償内容ブロックの必須項目が全てOKか判定
const isInsuranceAllOk = computed(() =>
  buildingInsAmountValue.value !== null &&
  householdInsAmountValue.value !== null
);

// 全ブロックの必須項目が全てOKか判定
const isAllConditionOk = computed(() =>
  buildingTypeValue.value !== 0 &&
  buildingStructureValue.value !== 0 &&
  buildingAgeValue.value !== 0 &&
  prefValue.value !== 0 &&
  buildingInsAmountValue.value !== null &&
  householdInsAmountValue.value !== null &&
  name1.value !== '' &&
  name2.value !== '' &&
  nameKana1.value !== '' &&
  nameKana2.value !== '' &&
  email.value !== '' &&
  nameKanjiError.value === '' &&
  nameKanaError.value === '' &&
  emailError.value === '' &&
  emailTouched.value === true
);

defineExpose({ isAllConditionOk });

// // 名前自動カナ入力（vanilla-autokana）
// let familyNameBind: any = null;
// let firstNameBind: any = null;

// watch(isInsuranceAllOk, async (ok) => {
//   if (ok) {
//     await nextTick();
//     const autokanaModule = await import('vanilla-autokana');
//     familyNameBind = autokanaModule.bind("#name1", "#nameKana1", { katakana: true });
//     firstNameBind = autokanaModule.bind("#name2", "#nameKana2", { katakana: true });
//   }
// });

// // フリガナ欄への反映
// watch(name1, (newVal) => {
//   if (familyNameBind) {
//     if (newVal === "") {
//       nameKana1.value = "";
//     } else {
//       nameKana1.value = familyNameBind.getFurigana();
//     }
//   }
// });
// watch(name2, (newVal) => {
//   if (firstNameBind) {
//     if (newVal === "") {
//       nameKana2.value = "";
//     } else {
//       nameKana2.value = firstNameBind.getFurigana();
//     }
//   }
// });

</script>

<template>
  <div class="conditionSelection-content">
    <div class="conditionSelection-select">
      <section class="conditionSelection-info">
        <h2>所有する建物の情報</h2>
        <ul>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': buildingTypeValue !== 0 }">建物種類</div>
            <ul class="conditionSelection-radioBox">
              <li v-for="item in buildingTypeList" :key="item.id">
                <input
                  :id="'buildingType_' + item.id"
                  v-model="buildingTypeValue"
                  type="radio"
                  name="buildingType"
                  :value="item.id"
                  @change="onBuildingTypeChange"
                />
                <label :for="'buildingType_' + item.id">{{ item.name }}</label>
              </li>
              <div>
                <ul class="conditionSelection-note">
                  <li>※<a href="https://direct.nisshinfire.co.jp/oheya?a1=852911&a2=" target="_blank" class="bold">賃貸物件の方はこちら</a><br>日新火災のお部屋を借りるときの保険（外部サイト）</li>
                  <li>※<a href="/hojin/step/step1.html" class="no-external bold" >法人向け火災保険をご希望の方はこちら</a></li>
                </ul>
              </div>
            </ul>
          </li>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': buildingStructureValue !== 0 }">建物構造
              <OrganismsCompareHelpModal
                :help-modal-items="helpModalItems[15]"
              />
            </div>
            <ul class="conditionSelection-radioBox">
              <li v-for="item in buildingStructureList" :key="item.id" :class="{ single: buildingStructureList.length === 1 }">
                <input
                  :id="'buildingStructure_' + item.id"
                  v-model="buildingStructureValue"
                  type="radio"
                  name="buildingStructure"
                  :value="item.id"
                  :disabled="buildingStructureList.length === 1"
                />
                <label :for="'buildingStructure_' + item.id">{{ item.name }}</label>
              </li>
            </ul>
          </li>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': buildingAgeValue !== 0 }">築年数
              <OrganismsCompareHelpModal
                :help-modal-items="helpModalItems[0]"
              />
            </div>
            <div class="conditionSelection-selectBox">
              <select
                id="buildingAge"
                v-model="buildingAgeValue"
                :class="{ selected: buildingAgeValue !== 0 }"
              >
                <option :value="0">築年数を選択</option>
                <option
                  v-for="item in buildingAgeList"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.name }}
                </option>
              </select>
              <div>
                <ul class="conditionSelection-note">
                  <li>※築36年以上の物件は本サービスのご利用対象外となります。<br>築年数によっては、次の火災保険でお見積りが可能です。(*1)</li>
                  <ul>
                    <li><a href="https://brg.sonysonpo.co.jp/fire/dlp/?30079501" target="_blank">・ソニー損保の新ネット火災保険（外部サイト）</a></li>
                    <li><a href="https://www.zurich.co.jp/agency/fire/?agencyCode=40840" target="_blank">・チューリッヒのネット火災保険（外部サイト）</a></li>
                  </ul>
                </ul>
              </div>
            </div>
          </li>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': prefValue !== 0 }">所在地</div>
            <div class="conditionSelection-selectBox">
              <select
                id="pref"
                v-model="prefValue"
                :class="{ selected: prefValue !== 0 }"
              >
                <option :value="0">所在地を選択</option>
                <option
                  v-for="pref in prefList"
                  :key="pref.id"
                  :value="pref.id"
                >
                  {{ pref.name }}
                </option>
              </select>
            </div>
          </li>
        </ul>
      </section>
      <section class="conditionSelection-insurance" v-if="isInfoAllOk">
        <h2>希望する保険金額・補償内容</h2>
        <ul>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': buildingInsAmountValue !== null }">建物保険金額
              <OrganismsCompareHelpModal
                :help-modal-items="helpModalItems[1]"
              />
            </div>
            <div class="conditionSelection-selectBox">
              <select
                id="buildingInsAmount"
                v-model="buildingInsAmountValue"
                :class="{ selected: buildingInsAmountValue !== null }"
              >
                <option :value="null">建物保険金額を選択</option>
                <option
                  v-for="item in buildingInsAmountList"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.name }}
                </option>
              </select>
            </div>
          </li>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': householdInsAmountValue !== null }">家財保険金額
              <OrganismsCompareHelpModal
                :help-modal-items="helpModalItems[2]"
              />
            </div>
            <div class="conditionSelection-selectBox">
              <select
                id="householdInsAmount"
                v-model="householdInsAmountValue"
                :class="{ selected: householdInsAmountValue !== null }"
              >
                <option :value="null">家財保険金額を選択</option>
                <option
                  v-for="item in householdInsAmountList"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.name }}
                </option>
              </select>
            </div>
          </li>
          <li class="conditionSelection-accordion">
            <!-- SP -->
            <div class="conditionSelection-accordionToggle sp-only" @click="toggleEarthquakeAndCompensation">
              <span>試算条件</span>
              <span class="conditionSelection-toggleText" :class="{ 'is-open': isEarthquakeAndCompensationOpen }" id="DirectStep_acc_sp">
                変更する
              </span>
            </div>
            <!-- PC -->
            <div class="conditionSelection-accordionToggle pc-only" @click="toggleEarthquakeAndCompensation">
              <span>試算条件</span>
            </div>
            <div class="conditionSelection-accordionSummary" v-if="!isEarthquakeAndCompensationOpen">
              <dl>
                <dt>地震保険：</dt>
                <dd>{{ hasEarthquakeInsuranceValue ? 'あり' : 'なし' }}</dd>
              </dl>
              <dl>
                <dt>補償内容：</dt>
                <dd>{{ selectedCompensations }}</dd>
              </dl>
            </div>
            <!-- PCの変更ボタン　アコーディオン開く -->
            <span class="conditionSelection-toggleText conditionSelection-toggleText--summary pc-only" :class="{ 'is-open': isEarthquakeAndCompensationOpen }" @click="toggleEarthquakeAndCompensation" id="DirectStep_acc_pc1">
              変更する
            </span>
            <!-- アコーディオンコンテンツ -->
            <template v-if="isEarthquakeAndCompensationOpen">
              <div class="conditionSelection-accordionContent">
                <div class="conditionSelection-accordionItem">
                  <div class="conditionSelection-title">地震保険
                    <OrganismsCompareHelpModal
                      :help-modal-items="helpModalItems[3]"
                    />
                  </div>
                  <ul class="conditionSelection-radioBox">
                    <li>
                      <input
                        id="hasEarthquakeInsurance_1"
                        v-model="hasEarthquakeInsuranceValue"
                        type="radio"
                        name="hasEarthquakeInsurance"
                        :value="true"
                      />
                      <label for="hasEarthquakeInsurance_1">あり</label>
                    </li>
                    <li>
                      <input
                        id="hasEarthquakeInsurance_2"
                        v-model="hasEarthquakeInsuranceValue"
                        type="radio"
                        name="hasEarthquakeInsurance"
                        :value="false"
                      />
                      <label for="hasEarthquakeInsurance_2">なし</label>
                    </li>
                    <!-- <div class="conditionSelection-selectBox">
                      <select
                        id="seismicYearsDiscount"
                        v-model="seismicYearsDiscountValue"
                      >
                        <option
                          v-for="item in seismicYearsDiscountList"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.name }}
                        </option>
                      </select>
                    </div> -->
                  </ul>
                </div>
                <div class="conditionSelection-accordionItem">
                  <div class="conditionSelection-title">補償内容</div>
                  <div class="conditionSelection-compensationWrap">
                    <ul class="conditionSelection-checkboxBox">
                      <li class="required">
                        火災・落雷<br>・破裂・爆発
                      </li>
                      <OrganismsCompareHelpModal
                        :help-modal-items="helpModalItems[8]"
                      />
                    </ul>
                    <ul class="conditionSelection-checkboxBox">
                      <li>
                        <input
                          id="windHailSnowDamage"
                          v-model="windHailSnowDamageValue"
                          type="checkbox"
                          name="windHailSnowDamage"
                          :true-value="true"
                          :false-value="false"
                        />
                        <label for="windHailSnowDamage">風災・ひょう災・雪災</label>
                      </li>
                      <OrganismsCompareHelpModal
                        :help-modal-items="helpModalItems[9]"
                      />
                    </ul>
                    <ul class="conditionSelection-checkboxBox">
                      <li>
                        <input
                          id="floodDisaster"
                          v-model="floodDisasterValue"
                          type="checkbox"
                          name="floodDisaster"
                          :true-value="true"
                          :false-value="false"
                        />
                        <label for="floodDisaster">水災</label>
                      </li>
                      <OrganismsCompareHelpModal
                        :help-modal-items="helpModalItems[7]"
                      />
                    </ul>
                    <ul class="conditionSelection-checkboxBox">
                      <li>
                        <input
                          id="theftFlyingWaterDisturbance"
                          v-model="theftFlyingWaterDisturbanceValue"
                          type="checkbox"
                          name="theftFlyingWaterDisturbance"
                          :true-value="true"
                          :false-value="false"
                        />
                        <label for="theftFlyingWaterDisturbance">盗難・物体飛来・水ぬれ・騒じょう</label>
                      </li>
                      <OrganismsCompareHelpModal
                        :help-modal-items="helpModalItems[10]"
                      />
                    </ul>
                    <ul class="conditionSelection-checkboxBox">
                      <li>
                        <input
                          id="damageContamination"
                          v-model="damageContaminationValue"
                          type="checkbox"
                          name="damageContamination"
                          :true-value="true"
                          :false-value="false"
                        />
                        <label for="damageContamination">破損・汚損</label>
                      </li>
                      <OrganismsCompareHelpModal
                        :help-modal-items="helpModalItems[6]"
                      />
                    </ul>
                    <div>
                      <ul class="conditionSelection-note">
                        <li>※「火災、落雷、破裂、爆発」はすべてのプランに含まれています。</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <!-- PCの変更ボタン　アコーディオン閉じる -->
                <span class="conditionSelection-toggleText conditionSelection-toggleText--content pc-only" :class="{ 'is-open': isEarthquakeAndCompensationOpen }" @click="toggleEarthquakeAndCompensation" id="DirectStep_acc_pc2">
                  変更する
                </span>
              </div>
            </template>
          </li>
        </ul>
      </section>
      <section class="conditionSelection-customer" v-if="isInsuranceAllOk">
        <h2>お客様の情報</h2>
        <ul>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': isNameKanjiValid }">お名前（漢字）</div>
            <div :class="['conditionSelection-customerFields', 'name', { 'validation-area': nameKanjiError }]">
              <div>
                <label for="name1">姓</label>
                <input
                  id="name1"
                  type="text"
                  placeholder="例）山田"
                  v-model="name1"
                  maxlength="62"
                  :class="{ 'error': nameKanjiError }"
                />
              </div>
              <div>
                <label for="name2">名</label>
                <input
                  id="name2"
                  type="text"
                  placeholder="例）太郎"
                  v-model="name2"
                  maxlength="62"
                  :class="{ 'error': nameKanjiError }"
                />
              </div>
            </div>
            <div v-if="nameKanjiError" class="validation-error" v-html="nameKanjiError"></div>
          </li>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': isNameKanaValid }">お名前（カナ）</div>
            <div :class="['conditionSelection-customerFields', 'nameKana', { 'validation-area': nameKanaError }]">
              <div>
                <label for="nameKana1">セイ</label>
                <input
                  id="nameKana1"
                  type="text"
                  placeholder="例）ヤマダ"
                  v-model="nameKana1"
                  maxlength="62"
                  :class="{ 'error': nameKanaError }"
                />
              </div>
              <div>
                <label for="nameKana2">メイ</label>
                <input
                  id="nameKana2"
                  type="text"
                  placeholder="例）タロウ"
                  v-model="nameKana2"
                  maxlength="62"
                  :class="{ 'error': nameKanaError }"
                />
              </div>
            </div>
            <div v-if="nameKanaError" class="validation-error" v-html="nameKanaError"></div>
          </li>
          <li>
            <div class="conditionSelection-title" :class="{ 'is-valid': isEmailValid && !emailError }">メールアドレス</div>
            <div :class="['conditionSelection-customerFields', 'email', { 'validation-area': emailError }]">
              <input
                id="email"
                type="email"
                placeholder="例）yamada@bang-inc.jp"
                v-model="email"
                maxlength="255"
                :class="{ 'error': emailError }"
                @blur="validateEmailOnBlur"
                @keyup.enter="validateEmailOnBlur"
                @input="handleEmailInput"
              />
            </div>
            <div v-if="emailError" class="validation-error" v-html="emailError"></div>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.conditionSelection-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  margin-bottom: 2%;
  position: relative;
  padding-left: 11.66%;
  &::before,
  &::after {
    content: '';
    display: inline-block;
    position: absolute;
    left: 0;
    top: 50%;
    color: #fff;
    font-size: px(12);
    font-weight: normal;
    border-radius: 4px;
    transition: 0.3s all;
    box-sizing: border-box;
    backface-visibility: hidden;
    z-index: 2;
  }
  &::before {
    content: "必須";
    padding: 0.29% 1.17%;
    background: #ff0000;
    transform: translateY(-50%) rotateY(0deg);
    z-index: 3;
  }
  &::after {
    content: "OK";
    padding: 0.29% 2.04%;
    background: #31C400;
    transform: translateY(-50%) rotateY(-180deg);
    z-index: 2;
  }

  &.is-valid::before {
    transform: translateY(-50%) rotateY(-180deg);
    z-index: 2;
  }
  &.is-valid::after {
    transform: translateY(-50%) rotateY(0deg);
    z-index: 3;
  }

  br {
    display: none;
  }
}

.conditionSelection-select {
  padding: 0% 4.2%;
  h2{
    font-size: px(20);
    font-weight: bold;
    color: $colorCompareBlue;
    padding: 2.33% 0 4.66% 9.33%;
    border-top: 2px solid $colorCompareBlue;
  }
  > section{
    padding: 5.3% 0;
    &.conditionSelection-info{
      h2{
        background: url(~/assets/images/compare/icon_home.svg) no-repeat 0% 30%;
        background-size: 8% auto;
      }
    }
    &.conditionSelection-insurance{
      h2{
        background: url(~/assets/images/compare/icon_document-currency-yen.svg) no-repeat 0% 30%;
        background-size: 8% auto;
      }
    }
    &.conditionSelection-customer{
      h2{
        background: url(~/assets/images/compare/icon_user.svg) no-repeat 0% 30%;
        background-size: 9% auto;
      }
    }
    &:last-child {
      padding: 5.3% 0 11.66%;
    }
  }

  > section > ul > li {
    margin-bottom: 6.4%;

    &.twoColumn {
      align-items: center;
    }

    &:last-child {
      margin-bottom: 0;
    }

    select {
      -webkit-appearance: none;
      appearance: none;
      border: 1px solid #cccccc;
      border-radius: 4px;
      width: 69.97%;
      padding: 3.47% 4.27%;
      background: url(~/assets/images/compare/icon_chevron-down.svg) right 4.2%
        center no-repeat;
      color: #333;
    }
  }
}

.conditionSelection-radioBox {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  > li {
    flex: 1;
    margin-right: 1.8%;

    &:last-child {
      margin-right: 0;
    }

    input {
      display: none;

      &:checked + label {
        background: url(~/assets/images/compare/label_on.png) left 6% center
          no-repeat $colorSubBackground;
          border: 1px solid #1C79C6;
      }
    }

    label {
      padding: 7% 3.4% 7% 20%;
      border: 1px solid #cccccc;
      border-radius: 4px;
      display: block;
      background: url(~/assets/images/compare/label_off.png) left 6% center
        no-repeat #FFF;
    }
    &.single {
      input {
        &:checked + label {
          background: url(~/assets/images/compare/label_on.png) left 3% center
            no-repeat $colorSubBackground;
          border: 1px solid #cccccc;
        }
      }
      label {
        background: #F2F2F2 !important;
        padding: 3.47% 4.27%;
        width: 69.97%;
        box-sizing: border-box;
      }
    }
  }
  >div{
    width: 100%;
  }
}

.conditionSelection-accordion{
  border: 1px solid #cccccc;
  border-radius: 4px;
}
.conditionSelection-accordionToggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2.35% 2.35% 2.35% 8.8%;
  cursor: pointer;
  border-bottom: 1px solid #CCCCCC;
  background: url(~/assets/images/compare/icon_settinglabel.png) left 2% center no-repeat;
  background-size: 6% auto;
  
  span:first-child {
    font-weight: bold;
    font-size: px(14);
  }
  
  &.pc-only {
    .conditionSelection-toggleText {
      display: none;
    }
  }
}

.conditionSelection-toggleText {
  color: #1E86C6;
  font-size: px(12);
  font-weight: bold;
  position: relative;
  padding-right: 1.5em;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    width: 0.5em;
    height: 0.5em;
    border-top: 2px solid $colorCompareBlue;
    border-right: 2px solid $colorCompareBlue;
    transform: translateY(-70%) rotate(135deg);
    transition: transform 0.3s;
  }
  
  &.is-open::after {
    transform: translateY(-30%) rotate(-45deg);
  }
}

.pc-only {
  display: none;
}

.conditionSelection-accordionSummary {
  padding: 1.17% 4.69% 2.35%;
  background: #F7F8F8;
  border-radius: 0 0 4px 4px;
  
  dl {
    display: flex;
    color: #666666;
    font-size: px(12);
    
    dd {
      width: 77.87%;
    }
  }
}

.conditionSelection-accordionContent {
  background: #F7F8F8;
  padding: 4.66%;
  border-radius: 0 0 4px 4px;
  .conditionSelection-accordionItem {
    &:first-child{
      padding: 0 0 4.66%;
    }
    .conditionSelection-title{
      padding: 0;
      margin: 0 0 3.88%;
      font-size: px(14);
      &::before,
      &::after {
        content: none;
      }
    }
  }
}

.conditionSelection-compensationWrap {
  .conditionSelection-checkboxBox{
    display: flex;
    width: 100%;
    flex-wrap: nowrap;
    align-items: center;
    > li{
      width: 91.84%;
      margin: 0 1.4% 1.27% 0;
      position: relative;
      input {
        display: none;

        &:checked + label {
          background: url(~/assets/images/compare/checkbox_checked.png) left 4% center
            no-repeat $colorSubBackground;
          border: 1px solid #1C79C6;
          background-size: 5%;
        }
      }
      label {
        padding: 0 18.41% 0 15.87%;
        border: 1px solid #cccccc;
        border-radius: 4px;
        background: url(~/assets/images/compare/checkbox.png) left 4% center
          no-repeat #FFF;
        background-size: 5%;
        cursor: pointer;
        min-height: 16.53vw;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        font-size: px(14);
      }
      
      &::after {
        content: '';
        display: block;
        position: absolute;
        right: 4%;
        top: 50%;
        transform: translateY(-50%);
        width: 13%;
        padding-bottom: 13%;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        pointer-events: none;
      }
    }
    
    &:nth-child(1) li::after {
      background-image: url(~/assets/images/compare/iconCompensation_01.png);
    }
    &:nth-child(2) li::after {
      background-image: url(~/assets/images/compare/iconCompensation_02.png);
    }
    &:nth-child(3) li::after {
      background-image: url(~/assets/images/compare/iconCompensation_03.png);
    }
    &:nth-child(4) li::after {
      background-image: url(~/assets/images/compare/iconCompensation_04.png);
    }
    &:nth-child(5) li::after {
      background-image: url(~/assets/images/compare/iconCompensation_05.png);
    }
    .required{
      padding: 0 18.41% 0 14.56%;
      border: 1px solid #1C79C6;
      border-radius: 4px;
      background: $colorSubBackground;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      position: relative;
      min-height: 16.53vw;
      font-size: px(14);
      br{
        display: none;
      }
      
      &::before {
        content: '必須';
        position: absolute;
        left: 3%;
        top: 50%;
        transform: translateY(-50%);
        width: 10.2%;
        background-color: #1c79c6;
        color: #fff;
        font-size: px(12);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
      }
    }
  }
}

.conditionSelection-note {
  color: #666666;
  font-size: px(12);
  text-indent: -1em;
  padding: 3.2% 0 0 1em;
  >ul{
    padding: 0 0 0 1em;
  }
  a{
    color: $colorBlue;
    text-decoration: underline;
    background: url(~/assets/images/compare/icon_arrow-top-right-on-square_blue.svg) right center no-repeat;
    background-size: 1.5em;
    padding-right: 1.5em;
    &.no-external{
      background: none;
      vertical-align: text-top;
    }
    &:hover {
      opacity: 0.48;
    }
  }
  .bold{
    font-weight: bold;
  }
}

.conditionSelection-customerFields {
  display: flex;
  justify-content: space-between;
  >div{
    display: flex;
    align-items: center;
    justify-content: flex-end;

    label {
      font-size: px(14);
      font-weight: bold;
      margin: 0 8px 0 0;
    }
  }
  input {
    width: 74.13%;
    padding: 6.98% 9.3%;
    border: 1px solid #CCCCCC;
    border-radius: 4px;
    box-sizing: border-box;
    &::placeholder {
      color: #999999;
    }
    &.error {
      border-color: #FF0000;
    }
  }
  &.email{
    input {
      width: 100%;
      padding: 3.5% 4.66%;
    }
  }
}

.validation-error{
  color:#FF0000;
  font-size: px(12);
  margin-top: 3.5%;
  padding: 0 0 0 5.25%;
  background: url(~/assets/images/compare/icon_exclamation-triangle.svg) no-repeat 0 1px;
  background-size: 5% auto;
}


@include media(pc) {
  .conditionSelection-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
  }

  .conditionSelection-content {
    background-color: $colorDefaultBackground;
  }

  .conditionSelection-title {
    margin-bottom: 0;
    width: 181px;
    background: #EFEFEF;
    padding: 20px 12px 20px 56px;
    box-sizing: border-box;
    font-size: px(14);
    &::before,
    &::after {
      left: 16px;
    }
    &::before{
      padding: 1px 4px;
    }
    &::after {
      padding: 1px 7px;
    }

    br {
      display: block;
    }
  }

  .conditionSelection-select {
    padding: 0;
    box-sizing: border-box;
    h2{
      border: none;
      padding: 0 0 16px 32px;
    }
    > section{
      padding: 32px 0 0;
      &.conditionSelection-info{
        h2{
          background: url(~/assets/images/compare/icon_home.svg) no-repeat 0 2px;
          background-size: 3% auto;
        }
      }
      &.conditionSelection-insurance{
        h2{
          background: url(~/assets/images/compare/icon_document-currency-yen.svg) no-repeat 0 2px;
          background-size: 3% auto;
        }
      }
      &.conditionSelection-customer{
        h2{
          background: url(~/assets/images/compare/icon_user.svg) no-repeat 0 2px;
          background-size: 3.5% auto;
        }
      }
      &:last-child {
        padding: 32px 0 57px;
      }
    }

    > section > ul > li {
      display: flex;
      margin-bottom: 0;
      border-left: 1px solid #CCCCCC;
      border-right: 1px solid #CCCCCC;
      border-bottom: 1px solid #CCCCCC;
      position: relative;

      &:first-child {
        border-top: 1px solid #CCCCCC;
      }

      select {
        width: 240px;
        height: 48px;
        padding: 0 13px;

        &:hover {
          background-color: $colorSubBackground;
          cursor: pointer;
        }
      }
    }
  }

  .conditionSelection-accordion{
    border: none;
    border-radius: 0px;
  }

  .conditionSelection-accordionToggle {
    width: 181px;
    padding: 17px 12px 17px 44px;
    box-sizing: border-box;
    background: url(~/assets/images/compare/icon_settinglabel.png) left 16px center no-repeat #EFEFEF;
    background-size: 13% auto;
    
    &.pc-only {
      border-bottom: none;
      display: flex;
    }
  }

  .conditionSelection-toggleText {
    padding: 9px 30px 9px 16px;
    position: absolute;
    right: 16px;
    bottom: 0;
    display: inline-block;
    cursor: pointer;
    font-size: px(12);
    border: 1px solid #E6E6E6;
    border-bottom:none;
    border-radius: 8px 8px 0 0;
    
    &::after {
      right: 14px;
    }

    &:hover{
      background: #F7F8F8;
    }
  }
  
  .sp-only {
    display: none;
  }
  

  .conditionSelection-accordionSummary {
    background: #FFF;
    margin: 8px 0 8px 16px;
    padding: 0;
    width: 644px;
    border-radius: 0;
    dl {  
      dd {
        width: 470px;
      }
    }
  }

  .conditionSelection-accordionContent {
    background: #FFF;
    position: relative;
    padding: 16px 0 24px 16px;
    border-radius: 0;

    .conditionSelection-radioBox{
      margin: 8px 0 16px;
    }

    .conditionSelection-compensationWrap{
      margin: 8px 0 0;
    }

    .conditionSelection-accordionItem {
      &:first-child {
        padding: 0;
      }
      .conditionSelection-title{
        background: #FFF;
        color: #999999;
        margin: 0 0 8px;
      }
    }
  }

  .conditionSelection-radioBox {
    margin: 8px 0 8px 16px;
    width: 644px;

    > li {
      flex: none;
      width: 200px;
      margin-right: 10px;

      input {
        &:checked:hover + label {
          background: url(~/assets/images/compare/label_on.png) left 6% center
            no-repeat $colorSubBackground;
          cursor: pointer;
        }

        &:hover + label {
          background: url(~/assets/images/compare/label_off.png) left 6% center
            no-repeat $colorSubBackground;
          cursor: pointer;
        }

        &:disabled:checked + label {
          background: url(~/assets/images/compare/label_on.png) left 6% center
            no-repeat #f0f0f0;
          border: 1px solid #cccccc;
          color: #999999;
          cursor: not-allowed;
        }

        &:disabled:checked:hover + label {
          background: url(~/assets/images/compare/label_on.png) left 6% center
            no-repeat #f0f0f0;
          cursor: not-allowed;
        }
      }
      label {
        padding: 10px 0 10px 36px;

      } 
      &.single {
        input {
          &:checked + label {
            background: url(~/assets/images/compare/label_on.png) left 6% center
              no-repeat $colorSubBackground;
          }
        }
        label {
          padding: 10px 0 10px 13px;
          width: 240px;
        }
      }
    }
  }

  .conditionSelection-selectBox {
    margin: 8px 0 8px 16px;
    width: 644px;
  }

  .conditionSelection-compensationWrap {
    margin: 8px 0 16px 16px;
    width: 644px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    .conditionSelection-checkboxBox{
      display: block;
      width: 122px;
      > li{
        width: 100%;
        margin: 0;
        input {
          &:checked + label {
            background-image: url(~/assets/images/compare/checkbox_checked.png);
            background-repeat: no-repeat;
            background-position: left 7px top 7px;
            background-color: $colorSubBackground;
            background-size: 15%;
          }
        }
        label {
          padding: 76px 12px 16px 12px;
          cursor: pointer;
          background-image: url(~/assets/images/compare/checkbox.png);
          background-repeat: no-repeat;
          background-position: left 7px top 7px;
          background-size: 15%;
          font-size: px(12);
          display: block;
          text-align: center;
          min-height: 130px;
          &:hover {
            background-color: $colorSubBackground;
            cursor: pointer;
          }
        }
        
        &::after {
          content: '';
          right: 25%;
          top: 35%;
          width: 46%;
          padding-bottom: 46%;
        }
      }
      
      .required{
        padding: 76px 12px 16px 12px;
        display: flex;
        align-items: center;
        font-size: px(12);
        display: block;
        text-align: center; 
        min-height: 130px;
        br{
          display: block;
        } 
        &::before {
          left: 0;
          top: 9%;
          width: 32px;
          height: 24px;
          font-size: px(15);
          border-radius: 3px 4px 4px 0;
        }
      }
      :deep(.helpModal-btn){
        margin: 8px auto 0;
      }
    }
  }

  .conditionSelection-note {
    padding: 12px 0 0 1em;
    li{
      width: 100%;
      height: auto;
    }
  }

  .conditionSelection-customerFields {
    width: 416px;
    margin: 0;
    padding: 8px 16px;
    input {
      width: 164px;
      padding: 12px 16px;
    }
    &.name{
      >div{
        &:first-child{
          margin-left: 14px;
        }
      }
    }
    &.email{
      input {
        width: 320px;
        padding: 12px 16px;
      }
    }
    &.validation-area{
      align-items: flex-start;
      height: 67px;
    }
  }
  .validation-error{
    position: absolute;
    bottom: 6px;
    left: 192px;
    margin: 0;
    padding: 0 0 0 18px;
    background-size: 2.7% auto;
    width: 626px;
  }
  .validation-error :deep(br) {
    display: none;
  }
}
</style>
